'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Playbook } from '@/lib/playbooks';
import Image from 'next/image';
import { Search } from 'lucide-react';

interface PlaybooksListProps {
    playbooks: Playbook[];
}

export function PlaybooksList({ playbooks }: PlaybooksListProps) {
    const [searchQuery, setSearchQuery] = useState('');

    const filteredPlaybooks = playbooks.filter(playbook => {
        if (!playbook) return false;
        const query = searchQuery.toLowerCase();

        const name = (playbook.name || '').toLowerCase();
        const industry = (playbook.industry || '').toLowerCase();
        const description = (playbook.description || '').toLowerCase();
        const tags = playbook.tags || [];

        return (
            name.includes(query) ||
            industry.includes(query) ||
            description.includes(query) ||
            tags.some((tag: string) => (tag || '').toLowerCase().includes(query))
        );
    });

    return (
        <>
            <div className="text-center mb-12">
                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                    Marketing Playbooks
                </h1>
                <p className="text-xl text-neutral-600 max-w-2xl mx-auto mb-8">
                    Decoded growth strategies from top tech companies. Learn how they acquired their first users and scaled to millions.
                </p>

                <div className="relative max-w-md mx-auto">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-neutral-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search companies, industries, or niches..."
                        className="block w-full pl-10 pr-3 py-3 border border-neutral-200 rounded-xl leading-5 bg-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200 shadow-sm hover:border-neutral-300"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <span className="text-xs text-neutral-400 font-medium">
                                {filteredPlaybooks.length} {filteredPlaybooks.length === 1 ? 'result' : 'results'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPlaybooks.map((playbook, index) => (
                    <Link
                        key={`${playbook.slug}-${index}`}
                        href={`/playbooks/${playbook.slug}`}
                        className="group block h-full"
                    >
                        <div className="h-full bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6 transition-all duration-200 hover:shadow-lg hover:-translate-y-1">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="relative w-12 h-12 rounded-xl overflow-hidden bg-neutral-100 p-2">
                                    <Image
                                        src={playbook.logo}
                                        alt={playbook.name}
                                        width={48}
                                        height={48}
                                        className="object-contain"
                                        unoptimized
                                    />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold group-hover:text-blue-600 transition-colors">
                                        {playbook.name}
                                    </h2>
                                    <span className="text-xs font-medium px-2 py-1 bg-neutral-100 dark:bg-neutral-800 rounded-full text-neutral-600 dark:text-neutral-400">
                                        {playbook.industry}
                                    </span>
                                </div>
                            </div>

                            <p className="text-neutral-600 dark:text-neutral-400 mb-6 line-clamp-3">
                                {playbook.description}
                            </p>

                            <div className="flex flex-wrap gap-2 mt-auto">
                                {playbook.tags?.map((tag: string) => (
                                    <span key={tag} className="text-xs text-neutral-500 bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 px-2 py-1 rounded-md">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            {filteredPlaybooks.length === 0 && (
                <div className="text-center py-20">
                    <div className="bg-neutral-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Search className="h-8 w-8 text-neutral-400" />
                    </div>
                    <h3 className="text-lg font-medium text-neutral-900 mb-2">No playbooks found</h3>
                    <p className="text-neutral-600">Try adjusting your search query to find what you're looking for.</p>
                    <button
                        onClick={() => setSearchQuery('')}
                        className="mt-6 text-blue-600 font-medium hover:text-blue-700 underline-offset-4 hover:underline"
                    >
                        Clear search and see all
                    </button>
                </div>
            )}
        </>
    );
}
